import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, tap, catchError, throwError } from 'rxjs';

export interface KeycloakConfig {
  url: string;
  realm: string;
  clientId: string;
  redirectUri: string;
}

interface AppConfig {
  env: string;
  dishProductCode: string;
  API_URL: string;
  TOKEN_PATH: string;
  keycloak: KeycloakConfig;
  testMode: boolean;
  dishBaseUrl: string;
  privacyUrl: string;
  termsUrl: string;
  domain: string;
  whiteList: string[];
  adobeAnalyticsScript: string;
  googleMapsApiKey: string;
  [key: string]: any; // Allows accessing properties dynamically via get(key: string)
}

@Injectable({
  providedIn: 'root',
})
export class AppConfigService {
  private config!: AppConfig; // Using definite assignment assertion, ensure it's loaded before access
  private configLoaded = false;
  private configLoadError = false;

  constructor(private http: HttpClient) {}

  isProduction(): boolean {
    return this.configLoaded && this.config.env === 'prod';
  }

  /**
   * Loads the runtime configuration from 'assets/app.config.json'.
   * This method is typically called during application initialization via APP_INITIALIZER.
   * It handles both successful and unsuccessful loading, providing logging.
   * If loading fails, it throws an error, preventing the application from bootstrapping.
   * @returns An Observable that completes when the config is loaded.
   */
  loadConfig(): Observable<AppConfig> {
    const configPath = '/assets/app.config.json';

    return this.http.get<AppConfig>(configPath).pipe(
      tap(loadedConfig => {
        this.config = loadedConfig;
        this.configLoaded = true;
        console.log('Application configuration successfully loaded:', this.config.env);
      }),
      catchError((error: HttpErrorResponse) => {
        this.configLoaded = false;
        this.configLoadError = true; // Mark that an error occurred

        console.error(`Error loading application configuration from ${configPath}:`, error);

        // Throw an error to prevent the application from bootstrapping.
        console.error('Critical: Application cannot start without valid configuration. Halting.');
        return throwError(() => new Error(`Failed to load app configuration: ${error.message}`));
      })
    );
  }

  /**
   * Retrieves a configuration value by its key.
   * Throws an error if the configuration has not been loaded successfully,
   * or if a critical loading error occurred.
   * @param key The key of the configuration property to retrieve.
   * @returns The value associated with the key, or `null` if not found/accessible.
   */
  get<T>(key: string): T | null {
    if (this.configLoadError) {
      // If a critical error occurred during loading (and we chose Option 2),
      // the app should theoretically not even reach this point if APP_INITIALIZER
      // successfully halted it. But as a safeguard:
      console.error(`Attempted to access configuration '${key}' after a critical loading error.`);
      throw new Error('Application configuration is not available due to a critical loading error.');
    }
    if (!this.configLoaded) {
      // This scenario indicates a problem with APP_INITIALIZER setup or accessing config too early.
      console.error('Configuration not yet loaded. Ensure `loadConfig()` is called via APP_INITIALIZER.');
      throw new Error('Application configuration is not yet loaded.');
    }
    return (this.config[key] as T) || null;
  }

  /**
   * Checks if the configuration was successfully loaded.
   * @returns True if configuration was loaded, false otherwise.
   */
  isConfigLoaded(): boolean {
    return this.configLoaded;
  }

  /**
   * Checks if an error occurred during configuration loading.
   * @returns True if an error occurred, false otherwise.
   */
  hasConfigLoadError(): boolean {
    return this.configLoadError;
  }
}
